// @flow

import React, { useRef, useEffect } from "react";
import { css } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import {
  clearAllBodyScrollLocks,
  disableBodyScroll,
  enableBodyScroll,
} from "body-scroll-lock";
import { media } from "@nested/rebrand";

type Props = {|
  "data-test"?: string,
  open: boolean,
  onClose: () => void,
  children: React$Node,
  className?: string,
  css?: any,
|};

const backgroundStyle = css`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100%;
  min-height: 100vh;
  box-sizing: border-box;
  padding: 80px calc(0.08 * 100vw);
  z-index: 100;
  overflow-y: auto;
  padding-bottom: 50px;
  ${({ open }) =>
    open
      ? css`
          transition: background-color 300ms linear 0ms,
            visibility 300ms linear 0ms;
          visibility: visible;
          background-color: rgb(10, 66, 84, 0.8);
          pointer-events: auto;
        `
      : css`
          transition: background-color 300ms ease 300ms,
            visibility 300ms ease 300ms;
          visibility: hidden;
          background-color: rgb(10, 66, 84, 0);
          pointer-events: none;
        `};

  ${media.tablet`
    padding: calc(40px + 8vh) 20px;
  `}
`;

const modalStyle = css`
  position: relative;
  background-color: white;
  border-radius: 5px;
  width: 100%;
  max-width: 650px;
  padding: 20px;
  box-sizing: border-box;
  overflow: visible;
  margin: 0 auto 50px;

  ${({ open }) =>
    open
      ? css`
          transition: opacity 300ms ease 300ms, transform 300ms ease 300ms;
          transform: translateY(0px);
          opacity: 1;
        `
      : css`
          transition: opacity 300ms ease 0ms, visibility 300ms ease 0ms,
            transform 300ms ease 0ms;
          transform: translateY(3em);
          opacity: 0;
        `}

  ${media.desktop`
    padding: 50px;
  `}
`;

const closeButtonStyle = css`
  position: absolute;
  top: -40px;
  right: 0px;
  border: 0px;
  background-color: ${({ theme }) => theme.palette.hague50};
  border-radius: 17px;
  cursor: pointer;
  color: white;
  font-size: 16px;
  line-height: 24px;
  padding: 4px 30px 4px 12px;
  font-weight: 500;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-sizing: border-box;
`;

const iconStyle = css`
  position: absolute;
  height: 24px !important;
  top: 4px;
  right: 12px;
`;

export const Modal = ({
  open,
  onClose,
  children,
  className,
  "data-test": dataTest,
}: Props) => {
  const modalRef = useRef();
  useEffect(() => {
    if (open) {
      disableBodyScroll(modalRef.current);
    } else {
      enableBodyScroll(modalRef.current);
    }
  }, [open]);

  useEffect(() => {
    // Callback for unmount of the modal to ensure locks aren't left over,
    // for example when clicking a link inside a modal
    return () => {
      clearAllBodyScrollLocks();
    };
  }, []);

  return (
    <div
      role="presentation"
      css={backgroundStyle}
      open={open}
      onClick={onClose}
      ref={modalRef}
    >
      <div
        role="presentation"
        open={open}
        onClick={(e) => e.stopPropagation()}
        css={modalStyle}
        className={className}
        data-test={dataTest}
      >
        <button
          data-test="close-modal"
          css={closeButtonStyle}
          onClick={onClose}
        >
          Close
          <FontAwesomeIcon css={iconStyle} icon={faTimes} />
        </button>
        {children}
      </div>
    </div>
  );
};
